
import './style.scss';
import { Add, Delete, Edit, FilterAlt, Refresh, Warning } from "@mui/icons-material";
import { Avatar, Box, Grid, IconButton } from "@mui/material";
import DataGrid from '../../../../components/DataGrid';
import ContentTitle from "../../../../components/Content/ContentTitle";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import IUser from "../../../../interfaces/IUser";
import { useEffect, useState, useCallback } from "react";
import UserService from "../../../../services/UserService";
import FormHelper from "../../../../helpers/FormHelper";
import RegistrationStatus from "../../../../components/RegistrationStatus";
import AddEditDialog from "./AddEditDialog";
import { useUserContext } from "../../../../contexts/UserContext";
import RemoveDialog from "./RemoveDialog";
import DefaultConstants from "../../../../data/Constants";
import { INavigationLink } from "../../../../interfaces/INavigation";
import Filters from './Filters';
import { RegistrationStatusEnum } from '../../../../data/Enums';
import IEnterprise from '../../../../interfaces/IEnterprise';
import EnterpriseService from '../../../../services/EnterpriseService';
import Tooltip from '../../../../components/Tooltip';
import useDrawer from '../../../../hooks/useDrawer';
import useDialog from '../../../../hooks/useDialog';


const User = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { user, userPermissions } = useUserContext();

    const filtersDrawer = useDrawer();
    const [filters, setFilters] = useState<FormData | undefined>(undefined);
    
    const [data, setData] = useState<IUser[]>([]);
    const [selectedRow, setSelectedRow] = useState<IUser | undefined>(undefined);

    const addEditDialog = useDialog();
    const removeDialog = useDialog();

    const [enterprises, setEnterprises] = useState<IEnterprise[]>([]);


    useEffect(() => {
        handleRefresh();

        if (enterprises.length === 0) {
            const formData = FormHelper.Create(undefined);
            EnterpriseService.List(formData)
                .then(resp => {
                    setEnterprises(resp.data.enterprises as IEnterprise[]);
                });
        }

        // eslint-disable-next-line
    }, [filters]);
    
    
    const handleRefresh = useCallback(() => {
        const formData = filters ?? FormHelper.Create(undefined);

        UserService.List(formData)
            .then((resp) => {
                setData(resp.data.users as IUser[]);
            });
        
        // eslint-disable-next-line
    }, [filters]);

    const handleAddEdit = useCallback((row: IUser | undefined) => {
        setSelectedRow(row);
        addEditDialog.handleOpen();

        // eslint-disable-next-line
    }, []);

    const handleRemove = useCallback((row: IUser) => {
        setSelectedRow(row);
        removeDialog.handleOpen();

        // eslint-disable-next-line
    }, []);


    const dataGridColumns = [
        { 
            field: 'avatar', 
            sortable: false, 
            headerName: '', 
            width: 80,
            renderCell: (params: any) => {
                return <Box className='width-100pc display-flex justifycontent-center'>
                    <Avatar
                        alt={params.row.fullName.toUpperCase()} 
                        src={params.row.avatar} 
                        className='fontsize-14'
                    />
                </Box>;
            }
        }, { 
            field: 'fullName', 
            headerName: translate('fullName'),
            flex: 1, 
            minWidth: 200
        }, { 
            field: 'login', 
            headerName: translate('user'),
            flex: 1, 
            minWidth: 120
        }, { 
            field: 'email', 
            headerName: translate('email'),
            flex: 1, 
            minWidth: 320
        }, { 
            field: 'partnerId',  
            headerName: translate('partnerId'), 
            flex: 1, 
            minWidth: 200,
            renderCell: (params: any) => {
                if (!params.row.partnerId) {
                    if (params.row.administrator) {
                        return translate('undefined');
                    }

                    return <Tooltip title={translate('_userPartnerIdEmpty')} placement="bottom">
                        <IconButton className='actionbutton-warning marginX-none'>
                            <Warning fontSize='small' />
                        </IconButton>
                    </Tooltip>;
                }
                
                return params.row.partnerId;
            }
        }, { 
            field: 'registrationStatusId', 
            type: DefaultConstants.dataGridRegistrationStatusType, 
            headerName: translate('situation'), 
            flex: 1, 
            minWidth: 200, 
            renderCell: (params: any) => {
                return <RegistrationStatus id={params.row.registrationStatusId} />;
            }
        }, { 
            field: 'actions', 
            type: 'actions', 
            headerName: translate('actions'), 
            width: 200, 
            renderCell: (params: any) => {
                if (params.row.administrator && !user?.administrator) {
                    return;
                }

                if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                    return;
                }

                return (
                    <Grid item>
                        {
                            userPermissions.includes(`${linkProps.alias[0]}.Edit`) && (
                                <Tooltip title={translate('edit')}>
                                    <IconButton className='actionbutton-edit' onClick={() => handleAddEdit(params.row)}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {
                            !params.row.administrator && userPermissions.includes(`${linkProps.alias[0]}.Remove`) && (
                                <Tooltip title={translate('remove')}>
                                    <IconButton className='actionbutton-remove' onClick={() => handleRemove(params.row)}>
                                        <Delete fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                    </Grid>
                );
            }
        }
    ];

    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle {...linkProps} />
                </Grid>

                <Grid item className='grid-tools'>
                    {
                        userPermissions.includes(`${linkProps.alias[0]}.Add`) && (
                            <Tooltip title={translate('add')}>
                                <IconButton onClick={() => handleAddEdit(undefined)}>
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                    
                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filtersDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid container className='grid-content'>
                <Grid item xs={12} className='paper-content'>
                    <DataGrid 
                        columns={dataGridColumns} 
                        data={data} 
                        openSearch={false}
                        onCellDoubleClick={params => {
                            if (params.row.administrator && !user?.administrator) {
                                return;
                            }

                            if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                                return;
                            }

                            if (userPermissions.includes(`${linkProps.alias[0]}.Edit`)) {
                                handleAddEdit(params.row);
                                return;
                            }
                            
                            if (userPermissions.includes(`${linkProps.alias[0]}.Remove`)) {
                                handleRemove(params.row);
                                return;
                            }
                            
                            handleAddEdit(params.row);
                        }}
                    />
                </Grid>
            </Grid>

            <Filters
                open={filtersDrawer.open}
                handleClose={() => filtersDrawer.handleClose()}
                handleApplyFilters={(formData) => setFilters(formData)}
                enterprises={enterprises}
            />

            <AddEditDialog
                open={addEditDialog.open}
                handleClose={() => addEditDialog.handleClose()}
                data={selectedRow}
                handleListRefresh={handleRefresh}
                enterprises={enterprises}
            />

            <RemoveDialog
                open={removeDialog.open}
                handleClose={() => removeDialog.handleClose()}
                data={selectedRow}
                handleListRefresh={handleRefresh}
            />
        </>
    );
}

export default User;